import { action, computed, makeObservable, observable } from 'mobx';
import { ERROR_CODES, extractServerErrorCode } from '../utils/errorHandler';
import { RootStore } from './root';
import { AuthenticateByCodeStore } from './authenticateByCode';
import { TWO_FA_METHODS_MAP } from './enterCode';
import {
  loginTwoFactorAuthenticationVerifyClick,
  loginTwoFactorAuthenticationVerifySuccess,
  loginTwoFactorAuthenticationVerifyFail,
  loginTwoFactorAuthenticationResendCodeClick,
  loginTwoFactorAuthenticationLostPhoneAccessClick,
} from '@wix/bi-logger-hls2/v2';
import {
  LOST_ACCESS_TO_EMAIL_ARTICLE_URL,
  LOST_ACCESS_TO_PHONE_ARTICLE_URL,
  TwoFactorAuthMethods,
  TwoFactorAuthPhoneDeliveryMethods,
  twoFAProcessMethod,
  TWOFA_PROCESS,
} from '../utils/constants';

export class TwoFactorAuthStore extends AuthenticateByCodeStore {
  twoFAToken: string;
  usingTwoFAFallback: boolean;
  allowRememberMe: boolean = true;
  dialogTitleKey: string = 'enter_code.title';

  constructor(rootStore: RootStore) {
    super(rootStore);
    this.SUBMIT_INTERACTION_NAME = 'verify-code-2fa';
    makeObservable(this, {
      currentTwoFAMethod: observable,
      enabledTwoFAMethods: observable,
      twoFAHint: observable,
      twoFAToken: observable,
      usingTwoFAFallback: observable,
      showMethodsSwitch: computed,
      setAuthParams: action,
      verify: action,
      resendCode: action,
      onLostAccess: action.bound,
    });
  }

  setAuthParams(payload: Partial<TwoFactorAuthPayload>) {
    this.twoFAHint = payload.twoFAHint!;
    this.twoFAToken = payload.twoFAToken!;
    this.usingTwoFAFallback = !!payload.usingTwoFAFallback;
    this.currentTwoFAMethod =
      this.usingTwoFAFallback || !payload.currentTwoFAMethod
        ? TwoFactorAuthMethods.Email
        : payload.currentTwoFAMethod;
    this.rootStore.fedopsLogger.interactionStarted(twoFAProcessMethod(this.currentTwoFAMethod))
    this.enabledTwoFAMethods = payload.enabledTwoFAMethods?.reverse() ?? [
      this.currentTwoFAMethod,
    ];
  }

  onResendCodeClicked(
    options: { phoneDeliveryMethod?: string } = {
      phoneDeliveryMethod: TwoFactorAuthPhoneDeliveryMethods.SMS,
    },
  ) {
    this.rootStore.biLogger.report(
      loginTwoFactorAuthenticationResendCodeClick({
        flow_type: this.currentTwoFAMethod,
        tfa_type: 'login',
      }),
    );
    return this.resendCode(options);
  }

  async resendCode(
    options: { phoneDeliveryMethod?: string } = {
      phoneDeliveryMethod: TwoFactorAuthPhoneDeliveryMethods.SMS,
    },
  ) {
    const { phoneDeliveryMethod } = options;
    try {
      const data = await this.rootStore.apiStore.resendSecondFactorCode({
        twoFAToken: this.twoFAToken,
        twoFAMethod: this.currentTwoFAMethod,
        phoneDeliveryMethod,
      });
      if (data?.payload?.twoFAHint) {
        this.twoFAHint = data.payload?.twoFAHint;
      } else {
        return extractServerErrorCode(data);
      }
    } catch (error: any) {
      return extractServerErrorCode(error);
    }
  }

  verify({ code, rememberMe }: { code: string; rememberMe?: boolean }) {
    this.rootStore.biLogger.report(
      loginTwoFactorAuthenticationVerifyClick({
        remember_this_device: rememberMe,
        flow_type: this.currentTwoFAMethod,
        tfa_type: 'login',
      }),
    );
    this.rootStore.fedopsLogger.interactionStarted(
      this.SUBMIT_INTERACTION_NAME,
    );
    return this.rootStore.apiStore
      .verifyCodeAndLogin({
        twoFAMethod: this.currentTwoFAMethod,
        twoFAToken: this.twoFAToken,
        code,
        rememberMe,
      })
      .then((data) => {
        if (data.success) {
          this.rootStore.fedopsLogger.interactionEnded(
            this.SUBMIT_INTERACTION_NAME,
          );
          this.rootStore.fedopsLogger.interactionEnded(TWOFA_PROCESS)
          this.rootStore.fedopsLogger.interactionEnded(twoFAProcessMethod(this.currentTwoFAMethod))
          this.rootStore.biLogger.report(
            loginTwoFactorAuthenticationVerifySuccess({
              flow_type: this.currentTwoFAMethod,
            }),
          );
          this.rootStore.navigationStore.postLogin(data?.payload);
          return;
        }
        this.rootStore.biLogger.report(
          loginTwoFactorAuthenticationVerifyFail({
            flow_type: this.currentTwoFAMethod,
          }),
        );
        this.handleError(extractServerErrorCode(data));
      })
      .catch(() => {
        this.handleError(ERROR_CODES.GENERAL_ERROR_CODE);
      });
  }

  showResendButton() {
    return this.currentTwoFAMethod !== TwoFactorAuthMethods.TOTP;
  }

  getTitleKey() {
    return this.usingTwoFAFallback
      ? 'enter_code.fallback.title'
      : 'enter_code.title';
  }
  getDescriptionKey() {
    return this.usingTwoFAFallback
      ? 'enter_code.fallback.description'
      : `enter_code.${TWO_FA_METHODS_MAP[this.currentTwoFAMethod]}.description`;
  }

  enableAccess() {
    return !!(this.twoFAToken && this.currentTwoFAMethod);
  }

  onLostAccess() {
    const { language } = this.rootStore.i18n;
    this.rootStore.biLogger.report(
      loginTwoFactorAuthenticationLostPhoneAccessClick({
        flow_type: this.currentTwoFAMethod,
      }),
    );
    this.rootStore.navigationStore.redirect(
      this.currentTwoFAMethod === TwoFactorAuthMethods.Email
        ? LOST_ACCESS_TO_EMAIL_ARTICLE_URL(language)
        : LOST_ACCESS_TO_PHONE_ARTICLE_URL(language),
    );
  }

  get showMethodsSwitch() {
    return !this.usingTwoFAFallback && this.enabledTwoFAMethods?.length! > 1;
  }
}
